import { Alert } from '@akin-home/alerts';
import {
  createAlert,
  CreateAlertMutation,
  CreateAlertMutationVariables,
  DeleteAlertMutationVariables,
  graphqlCall,
} from '@akin-home/amplify';
import {
  createCreateMutationHook,
  createOptimisticUpdateMutationHook,
} from '@akin-home/utils/react-query';
import { API } from 'aws-amplify';
import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  alertModelToCreateGraphql,
  alertModelToUpdateData,
} from '../functions';
import { UpdateAlertInput } from '../types';
import { alertQueryKeys } from './queries';

export const updateAlertMutation = (params: { input: UpdateAlertInput }) =>
  API.put('CalendarAPI', `/alerts/${params.input?.id}`.replace('#', '%23'), {
    body: { input: params.input },
  });

export const deleteAlertMutation = (params: DeleteAlertMutationVariables) =>
  API.del('CalendarAPI', `/alerts/${params.input?.id}`.replace('#', '%23'), {});

export const createAlertMutation = (params: CreateAlertMutationVariables) =>
  graphqlCall<CreateAlertMutation>(createAlert, params);

export const useDeleteAlert = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteAlertMutation, {
    onSuccess() {
      // Since we are dealing with recurring items etc, just invalidate the queries
      queryClient.invalidateQueries<Alert[]>(alertQueryKeys.all);
    },
  });
};

export const useCreateAlert = createCreateMutationHook({
  keySet: alertQueryKeys,
  mutationFunc: createAlertMutation,
});

export const useUpdateAlert = createOptimisticUpdateMutationHook({
  keySet: alertQueryKeys,
  mutationFunc: updateAlertMutation,
  keyExtractor: (vars) => vars.input?.id,
  updater: ({ variables, item }) => {
    return {
      ...item,
      ...variables.input,
      start: variables.input?.start
        ? new Date(variables.input.start)
        : item.start,
    };
  },
});

export const useUpdateAlertFromModel = () => {
  const update = useUpdateAlert();

  return useCallback(
    async (alert: Alert) =>
      update.mutateAsync({ input: await alertModelToUpdateData(alert) }),
    [update]
  );
};

export const useCreateAlertFromModel = () => {
  const createEvent = useCreateAlert();

  return useCallback(
    async (event: Alert) =>
      createEvent.mutateAsync({
        input: await alertModelToCreateGraphql(event),
      }),
    [createEvent]
  );
};
